/**
 * Shop the feed Custom — Click-to-play + Mobile scroll-to-play
 *
 * Structure expected:
 *   <div class="stf-card" data-stf-click-play>
 *     <div class="stf-card__media">
 *       <video-element class="stf-card__video">...</video-element>
 *       <div class="stf-card__image">...preview + play icon...</div>
 *     </div>
 *     ...
 *   </div>
 *
 * Behaviour:
 *
 *  PC (desktop):
 *    - Click on card toggles play/pause (see handleCardClick).
 *    - No scroll-triggered autoplay.
 *
 *  Mobile (matches FoxTheme.config.mediaQueryMobile):
 *    - As the user scrolls (horizontally in the swiper, or vertically down
 *      the page), the single card with the largest visible area auto-plays.
 *      All other cards are paused. (Instagram-reel style.)
 *    - Tapping a card toggles play/pause, AND marks the card as
 *      "user-controlled" — from that point onward, scroll-driven autoplay
 *      will never touch that card again (it respects the user's choice).
 *    - When the viewport grows past the mobile breakpoint, scroll logic is
 *      torn down; it is re-installed if the user resizes back down.
 */
(function () {
  'use strict';

  var MOBILE_MQ_FALLBACK = '(max-width: 767px)';
  var AUTOPLAY_MIN_RATIO = 0.5; // ignore cards less than 50% visible

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  function getVideoEl(card) {
    return card.querySelector('video-element');
  }

  function playCard(card) {
    var videoEl = getVideoEl(card);
    if (!videoEl) return;
    card.classList.add('is-hovering');
    if (typeof videoEl.play === 'function') {
      try { videoEl.play(); } catch (err) {}
    }
  }

  function pauseCard(card) {
    var videoEl = getVideoEl(card);
    if (!videoEl) return;
    card.classList.remove('is-hovering');
    if (typeof videoEl.pause === 'function') {
      try { videoEl.pause(); } catch (err) {}
    }
  }

  // ---------------------------------------------------------------------------
  // Click-to-play (all viewports)
  // ---------------------------------------------------------------------------

  function handleCardClick(card, e) {
    // Let anchors and the product footer link behave normally.
    if (e.target.closest('a')) return;
    if (e.target.closest('.stf-card__footer')) return;

    var videoEl = getVideoEl(card);
    if (!videoEl) return;

    e.preventDefault();

    // Once the user has tapped, the card is user-controlled —
    // mobile scroll autoplay will stop touching it.
    card.__stfUserControlled = true;

    if (card.classList.contains('is-hovering')) {
      pauseCard(card);
    } else {
      playCard(card);
    }
  }

  function initClickToggle(card) {
    if (card.__stfClickInit) return;
    card.__stfClickInit = true;
    card.addEventListener('click', function (e) {
      handleCardClick(card, e);
    });
  }

  // ---------------------------------------------------------------------------
  // Mobile scroll-to-play (Instagram-reel style: single "best" card plays)
  // ---------------------------------------------------------------------------

  var scrollController = {
    observer: null,
    cards: [],      // cards currently managed
    ratios: null,   // WeakMap: card -> latest intersection ratio
    currentPlaying: null,
    rafPending: false,

    install: function (cards) {
      if (this.observer) return;
      if (!('IntersectionObserver' in window)) return;

      this.cards = Array.prototype.slice.call(cards);
      this.ratios = new WeakMap();

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.ratios.set(entry.target, entry.intersectionRatio);
          });
          self.scheduleUpdate();
        },
        {
          threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.6, 0.75, 0.9, 1.0],
          rootMargin: '0px',
        }
      );

      this.cards.forEach(function (card) {
        self.observer.observe(card);
      });
    },

    uninstall: function () {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
      // Pause any card we left playing.
      if (this.currentPlaying && !this.currentPlaying.__stfUserControlled) {
        pauseCard(this.currentPlaying);
      }
      this.currentPlaying = null;
      this.cards = [];
      this.ratios = null;
    },

    scheduleUpdate: function () {
      if (this.rafPending) return;
      this.rafPending = true;
      var self = this;
      (window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); })(function () {
        self.rafPending = false;
        self.update();
      });
    },

    update: function () {
      if (!this.observer) return;

      // Find the candidate card with the largest intersection ratio
      // that has NOT been user-controlled (tapped).
      var best = null;
      var bestRatio = 0;

      for (var i = 0; i < this.cards.length; i++) {
        var card = this.cards[i];
        if (card.__stfUserControlled) continue;
        var ratio = this.ratios.get(card) || 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          best = card;
        }
      }

      // If no card is sufficiently visible, pause whatever is playing.
      if (!best || bestRatio < AUTOPLAY_MIN_RATIO) {
        if (this.currentPlaying && !this.currentPlaying.__stfUserControlled) {
          pauseCard(this.currentPlaying);
        }
        this.currentPlaying = null;
        return;
      }

      // Same card still wins → nothing to do.
      if (this.currentPlaying === best) return;

      // Pause the previous winner.
      if (this.currentPlaying && !this.currentPlaying.__stfUserControlled) {
        pauseCard(this.currentPlaying);
      }

      // Play the new winner.
      playCard(best);
      this.currentPlaying = best;
    },
  };

  // ---------------------------------------------------------------------------
  // Multi-image slider (Swiper, inside individual cards)
  // ---------------------------------------------------------------------------

  function initImageSlider(sliderEl) {
    if (sliderEl.__stfImageSliderInit) return;
    sliderEl.__stfImageSliderInit = true;

    if (!(window.FoxTheme && window.FoxTheme.Carousel)) return;

    var prevEl = sliderEl.querySelector('[data-stf-image-prev]');
    var nextEl = sliderEl.querySelector('[data-stf-image-next]');
    var counterCurrent = sliderEl.querySelector('[data-stf-image-counter-current]');

    var options = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      watchSlidesProgress: true,
      threshold: 2,
      // PC uses arrows only (no drag); mobile uses touch swipe only (no arrows).
      allowTouchMove: isMobile(),
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      on: {
        slideChange: function (swiper) {
          if (counterCurrent) counterCurrent.textContent = String(swiper.realIndex + 1);
        },
      },
    };

    var instance = new window.FoxTheme.Carousel(sliderEl, options);
    instance.init();

    sliderEl.__stfImageSlider = instance;

    // Also recalc on breakpoint toggles, so allowTouchMove matches viewport.
    sliderEl.__stfSyncTouch = function () {
      if (instance && instance.slider) {
        instance.slider.allowTouchMove = isMobile();
      }
    };
  }

  function initImageSliders(scope) {
    var sliders = (scope || document).querySelectorAll('[data-stf-image-slider]');
    Array.prototype.forEach.call(sliders, initImageSlider);
  }

  function syncImageSlidersTouch(scope) {
    var sliders = (scope || document).querySelectorAll('[data-stf-image-slider]');
    Array.prototype.forEach.call(sliders, function (s) {
      if (typeof s.__stfSyncTouch === 'function') s.__stfSyncTouch();
    });
  }

  // ---------------------------------------------------------------------------
  // Mobile breakpoint handling (follows the theme's own slider pattern)
  // ---------------------------------------------------------------------------

  function getMobileMql() {
    var query =
      (window.FoxTheme && window.FoxTheme.config && window.FoxTheme.config.mediaQueryMobile)
        ? window.FoxTheme.config.mediaQueryMobile
        : MOBILE_MQ_FALLBACK;
    return window.matchMedia(query);
  }

  function isMobile() {
    if (window.FoxTheme && window.FoxTheme.config && typeof window.FoxTheme.config.mqlMobile === 'boolean') {
      return window.FoxTheme.config.mqlMobile;
    }
    return getMobileMql().matches;
  }

  function allCards(scope) {
    return (scope || document).querySelectorAll('[data-stf-click-play]');
  }

  function applyViewportMode(scope) {
    var cards = allCards(scope);
    if (isMobile()) {
      scrollController.install(cards);
    } else {
      scrollController.uninstall();
    }
    syncImageSlidersTouch(scope);
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function initAll(scope) {
    var cards = allCards(scope);
    Array.prototype.forEach.call(cards, initClickToggle);
    initImageSliders(scope);
    applyViewportMode(scope);
  }

  function init() {
    initAll(document);

    // Re-evaluate on breakpoint change (same hook the theme's own slider uses).
    var mql = getMobileMql();
    var handler = function () { applyViewportMode(document); };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(handler);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    if (!e || !e.target) return;
    initAll(e.target);
  });

  // Pause when tab is hidden — save bandwidth.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) return;
    var cards = allCards();
    Array.prototype.forEach.call(cards, function (c) {
      var v = getVideoEl(c);
      if (v && typeof v.pause === 'function') {
        try { v.pause(); } catch (err) {}
      }
    });
  });
})();
